import React, { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Edit2, MapPin, Trash2, FileText, Lock, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { collectionsApi } from '../services/collectionsApi'
import { useMergeSelection } from '../hooks/useMergeSelection'
import { useCollectionHealth } from '../hooks/useCollectionHealth'
import CollectionActionsMenu from '../components/collections/CollectionActionsMenu'
import RoundPlusButton from '../components/RoundPlusButton'
import DuplicateReview from '../components/collections/DuplicateReview'
import MergeSelectionBar from '../components/collections/MergeSelectionBar'
import MergeReviewModal from '../components/collections/MergeReviewModal'
import { formatMergeSummary, formatPruneSummary } from '../components/collections/cleanupSummaries'
import { useNotify } from '../components/notice/useNotify'
import MergeCheckbox from '../components/collections/MergeCheckbox'
import LocationDetailModal, {
    LocationDetailValue,
} from '../components/checkin/LocationDetailModal'
import LocationPickerModal from '../components/checkin/LocationPickerModal'
import EditPlaceById from '../components/collections/EditPlaceById'
import PlacesMap, {
    PlaceMapItem,
    PlacesMapViewMode,
} from '../components/places/PlacesMap'
import PlacesViewModeControl from '../components/places/PlacesViewModeControl'
import EditCollectionModal from '../components/collections/EditCollectionModal'
import { usePlacesMap } from '../hooks/usePlacesMap'
import type { CollectionWithItems } from '../types/collections'

/**
 * Custom view for the Places system collection. Three pieces:
 *
 *  - A global map (heatmap or clusters) that fuses every saved Place with
 *    every coords-only checkin attachment ("unnamed pins"). The cards
 *    below filter; the map never does — the point of the map is the
 *    overall footprint.
 *  - A pill selector to filter the cards by All / Public (Google-sourced)
 *    / Mine (user-named). Discrimination is via `source`, not the presence
 *    of a `google_place_id` (legacy rows can have both). Filter does not
 *    affect the map.
 *  - A toggle between Heatmap (density gradient — "where do I go a lot")
 *    and Clusters (grouped markers — "find a specific pin"). Heatmap is
 *    the default; both modes share the same dataset.
 *
 * Card tap opens LocationDetailModal (read-only viewer) with the Edit
 * affordance wired through to LocationPickerModal in edit mode. Saving
 * PUTs the existing collection_item — no new row, no orphan Place.
 */

type ViewMode = PlacesMapViewMode
type FilterMode = 'all' | 'public' | 'mine'

interface PlacesCollectionPageProps {
    /** The Places system collection (already fetched by the parent route).
     *  We only need its id for PUTs against items; everything else comes
     *  from /api/places/map. */
    collection: CollectionWithItems
    /** Admin viewers get a read-only schema affordance — the same
     *  EditCollectionModal as for user collections. Schema writes still 403
     *  on the backend. */
    isAdmin?: boolean
    /** Refetch trigger so the parent can reload after delete/edit if it
     *  also cares (e.g., for the header item count). Optional — the page
     *  manages its own /api/places/map state independently. */
    onCollectionRefresh?: () => void
}

const PlacesCollectionPage: React.FC<PlacesCollectionPageProps> = ({
    collection,
    isAdmin = false,
    onCollectionRefresh,
}) => {
    const navigate = useNavigate()
    const { places, unnamedPins, loading, error, reload: fetchData } = usePlacesMap()
    const [viewMode, setViewMode] = useState<ViewMode>('heatmap')
    const [filterMode, setFilterMode] = useState<FilterMode>('all')
    const [searchQuery, setSearchQuery] = useState('')

    // Modal state. The detail modal can open in two flavors:
    //  - via card tap: backed by a PlaceMapItem (we set selectedPlace)
    //  - via marker tap on an unnamed pin: bare coords (selectedPlace stays
    //    null; we open the detail modal with a coords-only value)
    const [selectedPlace, setSelectedPlace] = useState<PlaceMapItem | null>(null)
    const [detailCoords, setDetailCoords] = useState<{ lat: number; lng: number } | null>(null)
    const [editingPlace, setEditingPlace] = useState<PlaceMapItem | null>(null)
    // "Add place" entry point: opens the picker locked to create mode.
    // Independent of editingPlace — the two modals don't co-exist.
    const [creatingNewPlace, setCreatingNewPlace] = useState(false)
    const [editingCollection, setEditingCollection] = useState(false)
    const [deleteCandidate, setDeleteCandidate] = useState<PlaceMapItem | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [deleteRefCount, setDeleteRefCount] = useState<number | null>(null)

    // Entity-merge (de-duplication) multi-select. Selection survives the
    // All/Public/Mine filter changes and opening/closing the place peek.
    // Stage 6 wires `handleMergeClick` to the review modal.
    const merge = useMergeSelection()
    const [mergeReviewIds, setMergeReviewIds] = useState<number[] | null>(null)
    const notify = useNotify()
    const handleMergeClick = () => {
        setMergeReviewIds([...merge.selected])
    }

    // Prune-unreferenced multi-select (sibling of merge; uncapped, min 1). Lists
    // only zero-live-reference places, all pre-checked for deletion. Mutually
    // exclusive with merge — entering one exits the other.
    const prune = useMergeSelection({ min: 1, max: Infinity })
    // Counts for the two buttons below, so the page says whether either
    // would find anything before it is pressed. Refreshed after a prune or
    // a merge; a rename that makes or breaks a collision shows next load.
    const { health, refresh: refreshHealth } = useCollectionHealth(collection.id)
    // The grouped review behind the collision badge. Its own mode, exclusive
    // with merge and prune the way those two already are with each other.
    const [reviewingDuplicates, setReviewingDuplicates] = useState(false)
    const [pruneCandidateIds, setPruneCandidateIds] = useState<Set<number> | null>(null)
    const [pruneConfirmOpen, setPruneConfirmOpen] = useState(false)
    const [pruning, setPruning] = useState(false)
    const selectMode = merge.mergeMode || prune.mergeMode
    const activeSelection = prune.mergeMode ? prune : merge

    // Cards list — filtered. The map ignores the filter on purpose. In prune
    // mode the cards are further narrowed to the zero-reference candidate set.
    const filteredPlaces = useMemo(() => {
        let base = places
        if (filterMode === 'public') base = places.filter((p) => p.source === 'google')
        else if (filterMode === 'mine') base = places.filter((p) => p.source !== 'google')
        if (prune.mergeMode && pruneCandidateIds) {
            base = base.filter((p) => pruneCandidateIds.has(p.id))
        }
        const q = searchQuery.trim().toLowerCase()
        if (q) {
            base = base.filter((p) => {
                const haystack = `${p.name ?? ''} ${p.address ?? ''} ${p.category_name ?? ''} ${p.notes ?? ''}`.toLowerCase()
                return haystack.includes(q)
            })
        }
        return base
    }, [places, filterMode, searchQuery, prune.mergeMode, pruneCandidateIds])

    // Map data — every place with coords + every unnamed pin. Frequency
    // weighting: places carry checkin_count, pins carry 1.
    const mapPlaces = useMemo(() => places.filter((p) => p.lat != null && p.lng != null), [places])

    const detailValue = useMemo<LocationDetailValue | null>(() => {
        if (selectedPlace) {
            return {
                collectionItemId: selectedPlace.id,
                lat: selectedPlace.lat ?? undefined,
                lng: selectedPlace.lng ?? undefined,
                name: selectedPlace.name,
                address: selectedPlace.address,
                googlePlaceId: selectedPlace.google_place_id,
                categoryName: selectedPlace.category_name,
                categorySlug: selectedPlace.category_slug,
                notes: selectedPlace.notes,
            }
        }
        if (detailCoords) {
            return { lat: detailCoords.lat, lng: detailCoords.lng }
        }
        return null
    }, [selectedPlace, detailCoords])

    const handleDelete = async () => {
        if (!deleteCandidate) return
        setDeleting(true)
        try {
            // Confirm step always passes force=true. The earlier probe (no
            // force) only reads the reference count; the deletion itself is
            // gated on force regardless of whether references exist.
            await collectionsApi.items.delete(collection.id, deleteCandidate.id, true)
            setDeleteCandidate(null)
            setDeleteRefCount(null)
            await fetchData()
            if (onCollectionRefresh) onCollectionRefresh()
        } catch (err) {
            console.error('Failed to delete place:', err)
        } finally {
            setDeleting(false)
        }
    }

    // First click on the trash icon: ask the API whether the row has
    // referencing checkins. Backend returns can_delete=false + count when
    // there are references, so we surface that to the user before the
    // confirm step (matches CollectionDetailPage's flow).
    const startDelete = async (place: PlaceMapItem) => {
        setDeleteCandidate(place)
        setDeleteRefCount(null)
        try {
            const result = await collectionsApi.items.delete(collection.id, place.id)
            if (result?.can_delete === false) {
                setDeleteRefCount(result.reference_count || 0)
            } else {
                setDeleteRefCount(0)
            }
        } catch (err) {
            console.error('Reference check failed:', err)
            setDeleteRefCount(0)
        }
    }

    // --- Prune mode wiring -------------------------------------------------
    const exitPrune = () => {
        prune.exitMergeMode()
        setPruneCandidateIds(null)
    }
    const enterPrune = async () => {
        merge.exitMergeMode()
        const ids = places.map((p) => p.id)
        try {
            const counts = await collectionsApi.items.referenceCounts(collection.id, ids)
            const candidates = ids.filter((id) => (counts[id] ?? 0) === 0)
            setPruneCandidateIds(new Set(candidates))
            prune.selectAll(candidates)
            prune.enterMergeMode()
        } catch (err) {
            console.error('Failed to load reference counts for prune:', err)
        }
    }
    const toggleMerge = () => {
        if (merge.mergeMode) {
            merge.exitMergeMode()
            return
        }
        if (reviewingDuplicates) {
            setReviewingDuplicates(false)
            return
        }
        exitPrune()
        // When grouping has already found collisions, showing them beats opening
        // an empty multi-select and asking the user to find them by eye. With
        // none found there is nothing to show, so fall through to picking.
        //
        // Dismissed clusters count as something to show even when no LIVE one
        // is left: the review panel is the only route to Restore, so gating on
        // active clusters alone strands a collection that has ruled on all of
        // them — the misclick this was all supposed to stay recoverable from.
        if ((health?.duplicate_clusters.length ?? 0) > 0
            || (health?.dismissed_clusters.length ?? 0) > 0) {
            setReviewingDuplicates(true)
            return
        }
        merge.enterMergeMode()
    }
    const togglePrune = () => {
        if (prune.mergeMode) {
            exitPrune()
            return
        }
        // Third member of the same mutually-exclusive set.
        setReviewingDuplicates(false)
        void enterPrune()
    }
    const handlePruneConfirm = async () => {
        setPruning(true)
        try {
            const result = await collectionsApi.items.purgeUnreferenced(collection.id, [...prune.selected])
            exitPrune()
            setPruneConfirmOpen(false)
            await fetchData()
            if (onCollectionRefresh) onCollectionRefresh()
            notify.success(formatPruneSummary(result.reaped_ids.length, result.skipped_ids.length))
        } catch (err) {
            console.error('Failed to purge unreferenced places:', err)
            void refreshHealth()
        } finally {
            setPruning(false)
        }
    }

    return (
        <div className="min-h-screen bg-blush-light">
            <div className="max-w-7xl mx-auto px-4 pb-8 sm:pt-8">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                    <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate max-w-full">
                                    {collection.name}
                                </h1>
                                <span
                                    className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded"
                                    title="The structure of this collection is fixed. Honeyguide adds items here automatically as you tag locations on checkins."
                                >
                                    <Lock className="w-3 h-3" />
                                    Fixed structure
                                </span>
                            </div>
                            {collection.description && (
                                <p className="text-gray-600 mt-1">{collection.description}</p>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <CollectionActionsMenu
                                onEdit={isAdmin ? () => setEditingCollection(true) : undefined}
                                merge={places.length > 0 ? {
                                    active: merge.mergeMode || reviewingDuplicates,
                                    count: health?.duplicate_clusters.length,
                                    onToggle: toggleMerge,
                                } : undefined}
                                prune={places.length > 0 ? {
                                    active: prune.mergeMode,
                                    count: health?.orphan_count,
                                    onToggle: togglePrune,
                                } : undefined}
                            />
                            <RoundPlusButton onClick={() => setCreatingNewPlace(true)} label="Add place" />
                        </div>
                    </div>
                </div>

                {/* Map. Wait for the load to settle before mounting, so the
                    very first frame sees the real centroid (the underlying
                    <Map> treats defaultCenter/defaultZoom as one-shot). */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
                    <PlacesMap
                        places={mapPlaces}
                        unnamedPins={unnamedPins}
                        viewMode={viewMode}
                        loading={loading}
                        onPlaceClick={(p) => {
                            setDetailCoords(null)
                            setSelectedPlace(p)
                        }}
                        onPinClick={(c) => {
                            setSelectedPlace(null)
                            setDetailCoords(c)
                        }}
                    />
                </div>

                {reviewingDuplicates && health && (
                    <DuplicateReview
                        collectionId={collection.id}
                        clusters={health.duplicate_clusters}
                        dismissedClusters={health.dismissed_clusters}
                        // `usePlacesMap` rows aren't collection items; give the
                        // review the two fields it reads.
                        items={places.map((pl) => ({
                            id: pl.id,
                            data: { name: pl.name, notes: pl.notes },
                        }))}
                        onMerge={(ids) => setMergeReviewIds(ids)}
                        onChanged={refreshHealth}
                        onManualSelect={() => {
                            setReviewingDuplicates(false)
                            merge.enterMergeMode()
                        }}
                        onOpenDetail={(id) => {
                            const hit = places.find((pl) => pl.id === id)
                            if (hit) {
                                setDetailCoords(null)
                                setSelectedPlace(hit)
                            }
                        }}
                        onDone={() => setReviewingDuplicates(false)}
                    />
                )}

                {/* Toolbar */}
                {/* Below sm the pills take a full row and search + view settings
                    share the next; one row squeezed the pills inside their
                    clipped border. */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4 flex flex-wrap sm:flex-nowrap items-center gap-3">
                    <FilterPills value={filterMode} onChange={setFilterMode} counts={{
                        all: places.length,
                        public: places.filter((p) => p.source === 'google').length,
                        mine: places.filter((p) => p.source !== 'google').length,
                    }} />
                    <div className="flex-1 min-w-0 sm:min-w-[8rem] sm:max-w-xs">
                        <PlaceSearchInput value={searchQuery} onChange={setSearchQuery} />
                    </div>
                    <PlacesViewModeControl value={viewMode} onChange={setViewMode} />
                </div>

                {/* Cards */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
                    </div>
                ) : error ? (
                    <div className="text-rose-500 text-center py-12">{error}</div>
                ) : filteredPlaces.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
                        {places.length === 0
                            ? 'No places yet. Tag a location on a checkin to add one.'
                            : 'No places match this filter.'}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredPlaces.map((p) => (
                            <PlaceCard
                                key={p.id}
                                place={p}
                                mergeMode={selectMode}
                                selected={activeSelection.isSelected(p.id)}
                                onToggleSelect={() => activeSelection.toggle(p.id)}
                                onView={() => {
                                    setDetailCoords(null)
                                    setSelectedPlace(p)
                                }}
                                onEdit={() => setEditingPlace(p)}
                                onCheckins={() =>
                                    navigate(`/checkins?collection_item=${p.id}`)
                                }
                                onDelete={() => startDelete(p)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Merge selection bar — floats while in merge mode. "Merge" is a
                Stage-6 hand-off; here it only surfaces count + enable state. */}
                        <MergeSelectionBar
                visible={merge.mergeMode}
                count={merge.count}
                onMergeClick={handleMergeClick}
                onClear={merge.clear}
                onCancel={merge.exitMergeMode}
            />

            {/* Prune selection bar — the red "Delete N" sibling of the merge bar. */}
            <MergeSelectionBar
                visible={prune.mergeMode}
                count={prune.count}
                variant="prune"
                actionLabel="Delete"
                actionIcon={Trash2}
                minCount={1}
                maxCount={Infinity}
                hintText={{ empty: 'Select items to delete', belowMin: 'Select at least 1' }}
                onMergeClick={() => setPruneConfirmOpen(true)}
                onClear={prune.clear}
                onCancel={exitPrune}
            />

            {pruneConfirmOpen && (
                <PruneConfirm
                    count={prune.count}
                    deleting={pruning}
                    onCancel={() => setPruneConfirmOpen(false)}
                    onConfirm={handlePruneConfirm}
                />
            )}

            {mergeReviewIds && (
                <MergeReviewModal
                    collection={collection}
                    itemIds={mergeReviewIds}
                    onClose={() => setMergeReviewIds(null)}
                    onMerged={async (summary) => {
                        setMergeReviewIds(null)
                        merge.exitMergeMode()
                        await fetchData()
                        if (onCollectionRefresh) onCollectionRefresh()
                        void refreshHealth()
                        notify.success(
                            formatMergeSummary(
                                summary.repointed_checkin_count,
                                summary.reaped_loser_ids.length
                            )
                        )
                    }}
                />
            )}

            {/* Detail modal — used both for place cards and unnamed-pin
                marker taps. onEdit is wired only for *private* places:
                Public places (Google-backed) aren't editable here — the
                Google Place is the source of truth, and forking it into
                a private copy on every rename would create a sprawl of
                near-duplicates with no clear semantics. Unnamed pins are
                bare coords with no row to mutate. */}
            <LocationDetailModal
                open={detailValue != null}
                value={detailValue}
                onEdit={selectedPlace && selectedPlace.source !== 'google' ? () => {
                    const p = selectedPlace
                    setSelectedPlace(null)
                    setDetailCoords(null)
                    setEditingPlace(p)
                } : undefined}
                onClose={() => {
                    setSelectedPlace(null)
                    setDetailCoords(null)
                }}
            />

            {/* "Add place" entry point — picker locked to create mode. The
                modal handles /find_or_create itself; we just refresh after
                a successful confirm so the new row shows up in the cards
                and on the map. */}
            {creatingNewPlace && (
                <LocationPickerModal
                    open
                    lockedToCreate
                    existingPrivateNames={places
                        .filter((p) => p.source !== 'google')
                        .map((p) => p.name)}
                    onConfirm={async () => {
                        setCreatingNewPlace(false)
                        await fetchData()
                        if (onCollectionRefresh) onCollectionRefresh()
                    }}
                    onClose={() => setCreatingNewPlace(false)}
                />
            )}

            {editingPlace && (
                <EditPlaceById
                    collectionId={collection.id}
                    itemId={editingPlace.id}
                    onClose={() => setEditingPlace(null)}
                    onSuccess={async () => {
                        setEditingPlace(null)
                        await fetchData()
                        if (onCollectionRefresh) onCollectionRefresh()
                    }}
                />
            )}

            {/* Delete confirmation */}
            {deleteCandidate && (
                <DeleteConfirm
                    place={deleteCandidate}
                    refCount={deleteRefCount}
                    deleting={deleting}
                    onCancel={() => {
                        setDeleteCandidate(null)
                        setDeleteRefCount(null)
                    }}
                    onConfirm={handleDelete}
                />
            )}

            {editingCollection && (
                <EditCollectionModal
                    collection={collection}
                    onClose={() => setEditingCollection(false)}
                    onSuccess={async () => {
                        setEditingCollection(false)
                        await fetchData()
                        if (onCollectionRefresh) onCollectionRefresh()
                    }}
                />
            )}
        </div>
    )
}

// ------------------------------------------------------------------------
// Toolbar bits
// ------------------------------------------------------------------------

const FilterPills: React.FC<{
    value: FilterMode
    onChange: (v: FilterMode) => void
    counts: { all: number; public: number; mine: number }
}> = ({ value, onChange, counts }) => {
    const opts: { key: FilterMode; label: React.ReactNode; count: number }[] = [
        {
            key: 'all',
            label: <><span className="hidden sm:inline">All places</span><span className="sm:hidden">All</span></>,
            count: counts.all,
        },
        { key: 'public', label: 'Public', count: counts.public },
        { key: 'mine', label: 'Mine', count: counts.mine },
    ]
    return (
        <div className="flex sm:inline-flex w-full sm:w-auto rounded-md border border-gray-300 overflow-hidden text-sm">
            {opts.map((o, i) => (
                <button
                    key={o.key}
                    type="button"
                    onClick={() => onChange(o.key)}
                    className={`px-3 py-1.5 whitespace-nowrap flex-1 sm:flex-none ${i > 0 ? 'border-l border-gray-300' : ''} ${value === o.key
                            ? 'bg-action text-plum'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                >
                    {o.label}
                    <span className={`ml-1.5 text-xs ${value === o.key ? 'text-amber-100' : 'text-gray-500'}`}>
                        {o.count}
                    </span>
                </button>
            ))}
        </div>
    )
}

const PlaceSearchInput: React.FC<{
    value: string
    onChange: (v: string) => void
}> = ({ value, onChange }) => (
    <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Escape' && value) {
                    e.stopPropagation()
                    onChange('')
                }
            }}
            placeholder="Search places…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
        />
        <AnimatePresence>
            {value && (
                <motion.button
                    type="button"
                    onClick={() => onChange('')}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    whileHover={{ scale: 1.15, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                    aria-label="Clear search"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </motion.button>
            )}
        </AnimatePresence>
    </div>
)

// ------------------------------------------------------------------------
// Cards + delete confirm
// ------------------------------------------------------------------------

const PlaceCard: React.FC<{
    place: PlaceMapItem
    mergeMode?: boolean
    selected?: boolean
    onToggleSelect?: () => void
    onView: () => void
    onEdit: () => void
    onCheckins: () => void
    onDelete: () => void
}> = ({ place, mergeMode = false, selected = false, onToggleSelect, onView, onEdit, onCheckins, onDelete }) => {
    const isPublic = place.source === 'google'
    return (
        <div
            className={`relative bg-white rounded-lg shadow-sm border transition-colors ${
                mergeMode && selected
                    ? 'border-amber-500 ring-2 ring-action'
                    : 'border-gray-200 hover:border-amber-300'
            }`}
        >
            {mergeMode && (
                <div className="absolute top-2 left-2 z-10">
                    <MergeCheckbox checked={selected} onToggle={() => onToggleSelect?.()} />
                </div>
            )}
            <button
                onClick={onView}
                className="w-full text-left p-4 focus:outline-none focus:ring-2 focus:ring-action rounded-t-lg"
            >
                <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">{place.name}</div>
                        {place.address && (
                            <div className="text-xs text-gray-500 truncate mt-0.5">{place.address}</div>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
                            <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded ${isPublic
                                        ? 'bg-amber-50 text-amber-900'
                                        : 'bg-amber-50 text-amber-900'
                                    }`}
                            >
                                {isPublic ? 'Public' : 'Mine'}
                            </span>
                            {place.category_name && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                                    {place.category_name}
                                </span>
                            )}
                            <span className="text-gray-500">
                                {place.checkin_count} checkin{place.checkin_count === 1 ? '' : 's'}
                            </span>
                        </div>
                        {place.notes && (
                            <div className="text-xs text-amber-700 bg-amber-50 rounded px-1.5 py-1 mt-2 line-clamp-2 whitespace-pre-wrap">
                                {place.notes}
                            </div>
                        )}
                    </div>
                </div>
            </button>
            <div className="border-t border-gray-100 px-2 py-1.5 flex items-center justify-end gap-1">
                <button
                    onClick={onCheckins}
                    className="p-1 text-gray-600 hover:text-green-600"
                    title="View checkins for this place"
                >
                    <FileText className="w-4 h-4" />
                </button>
                {/* Public places are Google-sourced and not editable here.
                    Edit affordance is hidden so users don't try to rename a
                    canonical Google entity in their personal store. */}
                {!isPublic && (
                    <button
                        onClick={onEdit}
                        className="p-1 text-gray-600 hover:text-amber-900"
                        title="Edit place"
                    >
                        <Edit2 className="w-4 h-4" />
                    </button>
                )}
                <button
                    onClick={onDelete}
                    className="p-1 text-gray-600 hover:text-rose-600"
                    title="Delete place"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    )
}

const DeleteConfirm: React.FC<{
    place: PlaceMapItem
    refCount: number | null
    deleting: boolean
    onCancel: () => void
    onConfirm: () => void
}> = ({ place, refCount, deleting, onCancel, onConfirm }) => (
    <div
        className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
            if (e.target === e.currentTarget && !deleting) onCancel()
        }}
    >
        <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Delete place</h3>
            {refCount == null ? (
                <div className="flex items-center text-gray-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2" />
                    Checking for references…
                </div>
            ) : (
                <>
                    <p className="text-gray-700 mb-3">
                        Are you sure you want to delete "<span className="font-medium">{place.name}</span>"?
                    </p>
                    {refCount > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                            <p className="text-yellow-800 text-sm">
                                ⚠️ This place is referenced in {refCount} checkin{refCount === 1 ? '' : 's'}.
                                Deleting will detach those references.
                            </p>
                        </div>
                    )}
                </>
            )}
            <div className="flex justify-end gap-3 mt-3">
                <button
                    onClick={onCancel}
                    disabled={deleting || refCount == null}
                    className="px-4 py-2 text-gray-700 bg-secondary hover:bg-secondary-dark rounded-md disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    disabled={deleting || refCount == null}
                    className="px-4 py-2 bg-danger hover:bg-danger-dark text-white rounded-md disabled:opacity-50 flex items-center"
                >
                    {deleting ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            Deleting…
                        </>
                    ) : refCount && refCount > 0 ? (
                        'Delete Anyway'
                    ) : (
                        'Delete'
                    )}
                </button>
            </div>
        </div>
    </div>
)

// ------------------------------------------------------------------------
// Prune confirmation + toast copy
// ------------------------------------------------------------------------

const PruneConfirm: React.FC<{
    count: number
    deleting: boolean
    onCancel: () => void
    onConfirm: () => void
}> = ({ count, deleting, onCancel, onConfirm }) => (
    <div
        className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
            if (e.target === e.currentTarget && !deleting) onCancel()
        }}
    >
        <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Prune unreferenced items</h3>
            <p className="text-gray-700 mb-4">
                Delete {count} unreferenced item{count === 1 ? '' : 's'}? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
                <button
                    onClick={onCancel}
                    disabled={deleting}
                    className="px-4 py-2 text-gray-700 bg-secondary hover:bg-secondary-dark rounded-md disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    disabled={deleting}
                    className="px-4 py-2 bg-danger hover:bg-danger-dark text-white rounded-md disabled:opacity-50 flex items-center"
                >
                    {deleting ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            Deleting…
                        </>
                    ) : (
                        'Delete'
                    )}
                </button>
            </div>
        </div>
    </div>
)

export default PlacesCollectionPage
